import { Alert, CircularProgress } from '@mui/material';
import useFlexBoxStyles from 'common/flexBox';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bufferWhen, concatMap, from, fromEvent, of, share, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map } from 'rxjs/operators';
import YesNoDialog from '../../common/components/YesNoDialog';
import { useSupportedLangs } from '../../common/constants/langs';
import { getContentDialogMode, setContentDialogMode } from '../../redux/slices/appSlice';
import { getUser, testFeatureEnabled } from '../../redux/slices/authSlice';
import { closeCustomDialogAction, openCustomDialog } from '../../redux/slices/customDialogSlice';
import { getMicroFrontsConfig, getOrganization } from '../../redux/slices/organizationSlice';
import { getLanguage, getSettings } from '../../redux/slices/settingSlice';
import CreateFormAction from './actions/CreateFormAction';
import EditLeadAction from './actions/EditLeadAction';
import EditFormAction from './actions/EditFormAction';
import { useMicroFrontContext } from './MicroFrontContext';
import InstantCallContactAction from './actions/InstantCallContactAction';
import Drawer from '@mui/material/Drawer';
import CreateEventSessionAction from './actions/CreateEventSessionAction';
import config from '../../config';
import { getLicense } from '../../redux/slices/licenseSlice';
import api from '../../api';
import { callService } from '../../services/AirCallService';
import SettingsKey from '../../app/profile/settings/utils/settingsTypes';
import JoinEventAction from './actions/JoinEventAction';
import EditProductAction from './actions/EditProductAction';
import CreateProductAction from './actions/CreateProductAction';
import CreateShortVideoAction from './actions/CreateShortVideoAction';
import ConfirmationOptionsDialog from './ConfirmationOptionsDialog';

const useMicroFrontUrl = (slug) => {
    const microFrontsConfig = useSelector(getMicroFrontsConfig);
    return microFrontsConfig?.[slug]?.url;
};

/**
 * Alert id -> the microfrontend window that opened it, so its result can be
 * delivered back to that window and no other.
 *
 * `CLOSE_ALERT` is deliberately exempt from the `msg.source` filter below (an
 * alert card lives in its own iframe and must be able to close itself), so
 * every mounted MicroFront handles the message and each queues its own
 * `setAlerts` updater. React runs them in mount order: the first one to find
 * the alert fires its callback and removes it, leaving every later updater a
 * no-op. That makes the instance which answers "whichever mounted first" --
 * usually the routed page's MicroFront -- rather than the one whose iframe
 * opened the alert and owns the pending `correlationId`. The response then
 * lands in a window with no subscriber for that id and is dropped without a
 * trace, so the opener's `openAlert` observable never emits and the user's
 * Accept/Dismiss choice is silently lost.
 *
 * Looking the opener up here makes delivery independent of that race. It is
 * kept outside React, and off the alert object itself, because the alert is
 * structured-cloned into the alert card's iframe on REGISTER and a `Window`
 * reference cannot be cloned.
 */
const alertOpeners = new Map();

const Actions = Object.freeze({
    EDIT_LEAD: EditLeadAction,
    EDIT_FORM: EditFormAction,
    CREATE_FORM: CreateFormAction,
    INSTANT_CALL_CONTACT: InstantCallContactAction,
    CREATE_EVENT_SESSION: CreateEventSessionAction,
    JOIN_EVENT: JoinEventAction,
    EDIT_PRODUCT: EditProductAction,
    CREATE_PRODUCT: CreateProductAction,
    CREATE_SHORT_VIDEO: CreateShortVideoAction,
});

const DEFAULT_DRAWER_TRANSITION_DURATION = 300;
const DEFAULT_DRAWER_WIDTH = '30%';

const OAUTH_TOKEN_CHECK_DEBOUNCE_TIME = 500;

const MicroFront = ({ slug, path, query, url: urlProp, className, resizable, style, onReady, skipLoader, alert, startSize, handlerRef, data: dataProp, onDialogClose }) => {
    const iframe = useRef(null);
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const locale = useSelector(getLanguage);
    const { enqueueSnackbar } = useSnackbar();
    const origin = useRef('*');
    const [askForConfirmation, setAskForConfirmation] = useState(null);
    const [showConfirmation, setShowForConfirmation] = useState(null);
    const [dialog, setDialog] = useState(null);
    const languages = useSupportedLangs();
    const slugUrl = useMicroFrontUrl(slug);
    const [size, setSize] = useState({...startSize});
    const [ready, setReady] = useState(false);
    const user = useSelector(getUser);
    const organization = useSelector(getOrganization);
    const contentDialogMode = useSelector(getContentDialogMode);
    const settings = useSelector(getSettings);
    const license = useSelector(getLicense);
    const isTestFeatureEnabled = useSelector(testFeatureEnabled);
    const navigate = useNavigate();
    const [action, setAction] = useState(null);
    const {
        getMicroFrontState,
        updateMicroFrontState,
        openAlert: contextOpenAlert,
        closeAlert: contextCloseAlert,
        notifier,
    } = useMicroFrontContext();
    const [drawer, openDrawer] = useState(null);
    const [closingDrawer, setClosingDrawer] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const oauthTokenCheck = useRef(new Subject());
    const [callServiceRequest, setCallServiceRequest] = useState(null);
    
    const Action = useMemo(() => Actions[action?.key], [action]);

    const settingsHash = useMemo(
        () =>
            settings.reduce(
                (hash, setting) => {
                    hash[setting.settingKey] = setting.settingValue;
                    return hash;
                },
                { ready },
            ),
        [settings, ready],
    );

    const dispatch = useDispatch();

    const completeAction = useCallback(
        (result) => {
            setAction((currentAction) => {
                if (currentAction) {
                    iframe?.current?.contentWindow.postMessage(
                        {
                            __ONLIVE_MSG_KEY: 'RESPONSE',
                            correlationId: currentAction.correlationId,
                            result,
                        },
                        origin.current,
                    );
                }
                return null;
            });
        },
        [setAction],
    );

    const closeDialog = useCallback((result) => {
        setDialog((currentDialog) => {
            if (currentDialog) {
                iframe?.current?.contentWindow.postMessage(
                    {
                        __ONLIVE_MSG_KEY: 'RESPONSE',
                        correlationId: currentDialog.correlationId,
                        result,
                    },
                    origin.current,
                );
                setTimeout(() => dispatch(closeCustomDialogAction()));
            }
            return null;
        });
        onDialogClose?.(result);
        // eslint-disable-next-line
    },[dispatch]);

    const closeDrawer = useCallback((result) => openDrawer(currentDrawer => {
        if (currentDrawer) {
            setClosingDrawer(true);
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'RESPONSE',
                    correlationId: currentDrawer.correlationId,
                    result,
                },
                origin.current,
            );
            const exitDelay = typeof currentDrawer.transitionDuration === 'number'
                ? currentDrawer.transitionDuration
                : currentDrawer.transitionDuration?.exit || DEFAULT_DRAWER_TRANSITION_DURATION;
            setTimeout(() => {
                openDrawer(null);
                setClosingDrawer(false);
            }, exitDelay);
        }
        
        return currentDrawer;
    }), [openDrawer, setClosingDrawer]);

    /**
     * Answers a pending `openAlert` on the window that opened it. Falls back to
     * this instance's own iframe when the opener is unknown -- an alert opened
     * by the shell itself rather than by a microfrontend.
     */
    const respondToOpener = useCallback((correlationId, result, opener) => {
        const target = opener?.source ?? iframe?.current?.contentWindow;
        target?.postMessage(
            {
                __ONLIVE_MSG_KEY: 'RESPONSE',
                correlationId,
                result,
            },
            opener?.origin ?? origin.current,
        );
    }, []);

    // `previousOpener` is the owner of the alert this one replaces, captured by
    // the caller before it claimed the id (see the OPEN_ALERT case).
    const openAlert = useCallback((alert, previousOpener) => contextOpenAlert(alert, previous => {
        if (previous) {
            respondToOpener(previous.correlationId, null, previousOpener);
        }
    }), [contextOpenAlert, respondToOpener]);

    const closeAlert = useCallback((id, result = null) => contextCloseAlert(id, alert => {
        if (alert) {
            const opener = alertOpeners.get(id);
            alertOpeners.delete(id);
            respondToOpener(alert.correlationId, result, opener);
        }
    }), [contextCloseAlert, respondToOpener]);

    const [handler, setHandler] = useState({
        openAlert,
        closeAlert,
        openDrawer,
        closeDrawer,
    });

    useEffect(() => {
        setHandler({ openAlert, closeAlert, openDrawer, closeDrawer });
    }, [openAlert, closeAlert, openDrawer, closeDrawer]);

    useEffect(() => {
        if (handlerRef) {
            if (typeof handlerRef === 'function') {
                handlerRef(handler);
            } else {
                handlerRef.current = handler;
            }
        }
    }, [handlerRef, handler]);
    
    useEffect(() => {
        if (dialog) {
            const {
                microFront = {},
                fillFullScreen = false,
                fullScreen = fillFullScreen,
                enableAutoClose = fullScreen,
                backspaceLabel,
                title,
                styles,
                resizable = !fullScreen,
                hideCloseIcon = false,
                fill = fillFullScreen,
            } = dialog;
            const { slug, path, query, url, data } = microFront;
            dispatch(
                openCustomDialog(MicroFront, {
                    contentProperties: {
                        slug,
                        path,
                        query,
                        url,
                        resizable,
                        className: fullScreen && !fill ? flexBox.padding5 : undefined,
                        data,
                    },
                    fullScreen,
                    enableAutoClose,
                    backspaceLabel,
                    onClose: () => closeDialog(null),
                    title,
                    titleClass: fullScreen ? flexBox.paddingLeft5 : undefined,
                    paperStyles: styles,
                    showCloseIcon: !hideCloseIcon,
                    fillContent: fill,
                }),
            );
        }
        // eslint-disable-next-line
    }, [dialog]);
    
    const aircallsEnabled = settingsHash[SettingsKey.ENABLE_AIRCALLS];
    
    useEffect(() => {
        if (callServiceRequest) {
            if (aircallsEnabled) {
                const { phone, organizationId, agentId, agentName } = callServiceRequest;
                const payload = { organizationId, agentId, agentName };
                if (phone && organizationId && agentId && agentName) {
                    callService(phone, payload);
                } else {
                    console.warn(`Discarding call service request with incompleted payload: ${JSON.stringify(payload)}`);
                }
            } else {
                console.warn(`Discarding call service request because aircalls are disabled: ${JSON.stringify(callServiceRequest)}`);
            }
        }
        setCallServiceRequest(null);
    }, [callServiceRequest, aircallsEnabled, setCallServiceRequest]);

    useEffect(() => {
        const shared = oauthTokenCheck.current.pipe(share());
        const subscription = shared.pipe(
            bufferWhen(() => shared.pipe(
                debounceTime(OAUTH_TOKEN_CHECK_DEBOUNCE_TIME),
            )),
            concatMap((checks) => from(api.get('auth/check/me')).pipe(
                map(({ data: { user } }) => {
                    checks.forEach(({ correlationId }) => {
                        iframe?.current?.contentWindow.postMessage({
                                __ONLIVE_MSG_KEY: 'RESPONSE',
                                correlationId,
                                result: user,
                            },
                            origin.current,
                        );
                    });
                    return user;
                }),
                catchError((error) => {
                    checks.forEach(({ correlationId }) => {
                        iframe?.current?.contentWindow.postMessage({
                            __ONLIVE_MSG_KEY: 'ERROR',
                            correlationId,
                            message: error.message,
                            data: error.response?.data,
                        }, origin.current);
                    });
                    return of(null);
                }),
            )),
        ).subscribe();
            
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        const subscription = fromEvent(window, 'message')
            .pipe(
                filter(
                    (msg) =>
                        msg.source === iframe.current?.contentWindow ||
                        msg.data.__ONLIVE_MSG_KEY === 'CLOSE_DIALOG' ||
                        msg.data.__ONLIVE_MSG_KEY === 'CLOSE_DRAWER' ||
                        msg.data.__ONLIVE_MSG_KEY === 'CLOSE_ALERT',  
                ),
            )
            .subscribe((msg) => {
                const { data } = msg;
                switch (data.__ONLIVE_MSG_KEY) {
                    case 'REGISTER':
                        origin.current = msg.origin;
                        msg.source?.postMessage(
                            {
                                __ONLIVE_MSG_KEY: 'RESPONSE',
                                correlationId: data.correlationId,
                                microFront: { resizable, alert, data: dataProp },
                                ...config,
                                searchParams: Object.fromEntries(searchParams),
                                user: {
                                    ...user,
                                    id: user.userId,
                                    fullName: user.name,
                                },
                                organization,
                            },
                            msg.origin,
                        );
                        setReady(true);
                        onReady?.(true);
                        break;
                    case 'ENQUEUE_SNACKBAR':
                        enqueueSnackbar(data.message, {
                            variant: data.variant,
                        });
                        break;
                    case 'ASK_FOR_CONFIRMATION':
                        setAskForConfirmation(data);
                        break;
                    case 'SHOW_CONFIRMATION':
                        setShowForConfirmation(data);
                        break;
                    case 'OPEN_DIALOG':
                        setDialog(data);
                        break;
                    case 'CLOSE_DIALOG':
                        closeDialog(data.result);
                        break;
                    case 'RESIZE':
                        setSize(data.size);
                        break;
                    case 'SET_CONTENT_DIALOG_MODE':
                        dispatch(setContentDialogMode(!!data.active));
                        break;
                    case 'NAVIGATE_TO':
                        dispatch(setContentDialogMode(false));
                        dispatch(closeCustomDialogAction());
                        navigate(data.path, data.options);
                        break;
                    case 'OPEN_URL':
                        window.open(data.url, data.target, data.features);
                        break;
                    case 'EXECUTE_ACTION':
                        setAction(data);
                        break;
                    case 'UPDATE_MICRO_FRONT_STATE':
                        updateMicroFrontState(slug, data.state);
                        break;
                    case 'OPEN_DRAWER':
                        openDrawer(data);
                        break;
                    case 'CLOSE_DRAWER':
                        closeDrawer(data.result);
                        break;
                    case 'OPEN_ALERT': {
                        // Unlike CLOSE_ALERT, this message is source-filtered, so
                        // `msg.source` is this instance's iframe: the opener that
                        // will be waiting on `data.correlationId`. Read the id's
                        // previous owner before claiming it, since re-opening an
                        // existing id resolves that older alert with `null`.
                        const previousOpener = alertOpeners.get(data.id);
                        alertOpeners.set(data.id, { source: msg.source, origin: msg.origin });
                        openAlert(data, previousOpener);
                        break;
                    }
                    case 'CLOSE_ALERT':
                        closeAlert(data.id, data.result);
                        break;
                    case 'SET_SEARCH_PARAMS':
                        setSearchParams(data.params);
                        break;
                    case 'CHECK_OAUTH_TOKEN':
                        oauthTokenCheck.current.next(data);
                        break;
                    case 'START_CALL_SERVICE':
                        setCallServiceRequest(data);
                        break;
                    default:
                        break;
                }
            });

        return () => subscription.unsubscribe();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (ready) {
            const subscription = notifier.subscribe((notification) => {
                iframe?.current?.contentWindow.postMessage(
                    {
                        __ONLIVE_MSG_KEY: 'NOTIFICATION',
                        notification,
                    },
                    origin.current,
                );
            });

            return () => subscription.unsubscribe();
        }
    }, [ready, notifier]);
    
    useEffect(() => {
        if (ready) {
            const subscription = getMicroFrontState(slug).subscribe((microFrontState) => {
                iframe?.current?.contentWindow.postMessage(
                    {
                        __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                        microFrontState,
                    },
                    origin.current,
                );
            });

            return () => subscription.unsubscribe();
        }
    }, [ready, getMicroFrontState, slug]);

    useEffect(() => {
        if (ready) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    locale,
                    languages: languages.map(({ languageCode: key, language: name, unsupportedTranscription }) => ({
                        key,
                        name,
                        unsupportedTranscription,
                    })),
                },
                origin.current,
            );
        }
    }, [ready, locale, languages]);

    useEffect(() => {
        if (ready && user) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    user: {
                        ...user,
                        id: user.userId,
                        fullName: user.name,
                    },
                },
                origin.current,
            );
        }
    }, [ready, user]);

    useEffect(() => {
        if (ready && organization) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    organization,
                },
                origin.current,
            );
        }
    }, [ready, organization]);

    useEffect(() => {
        if (ready) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    state: {
                        contentDialogMode,
                        testFeatureEnabled: isTestFeatureEnabled,
                    },
                },
                origin.current,
            );
        }
    }, [ready, contentDialogMode, isTestFeatureEnabled]);

    useEffect(() => {
        if (ready) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    settings: settingsHash,
                },
                origin.current,
            );
        }
    }, [ready, settingsHash]);

    useEffect(() => {
        if (ready) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    license,
                },
                origin.current,
            );
        }
    }, [ready, license]);

    useEffect(() => {
        if (ready) {
            iframe?.current?.contentWindow.postMessage(
                {
                    __ONLIVE_MSG_KEY: 'UPDATE_CONTEXT',
                    searchParams: Object.fromEntries(searchParams),
                },
                origin.current,
            );
        }
    }, [ready, searchParams]);

    const handleConfirmation = useCallback((correlationId, confirmation) => {
        iframe?.current?.contentWindow.postMessage(
            {
                __ONLIVE_MSG_KEY: 'RESPONSE',
                correlationId,
                confirmation,
            },
            origin.current,
        );
        setAskForConfirmation(null);
    }, []);

    const handleConfirmationOption = useCallback((correlationId, option) => {
        iframe?.current?.contentWindow.postMessage(
            {
                __ONLIVE_MSG_KEY: 'RESPONSE',
                correlationId,
                option,
            },
            origin.current,
        );
        setShowForConfirmation(null);
    }, []);

    const url = useMemo(() => {
        if (urlProp) {
            return urlProp;
        }
        if (slugUrl) {
            const url = new URL(slugUrl);
            if (path) {
                url.pathname = `${url.pathname}/${path.startsWith('/') ? path.substring(1) : path}`;
            }
            if (query) {
                Object.entries(query).forEach(([key, value]) => url.searchParams.append(key, value));
            }
            return url.toString();
        }
        return urlProp || '';
    }, [slugUrl, path, query, urlProp]);

    const {
        drawerMicroFront,
        paper,
        slide,
        transitionDuration,
        elevation,
        hideBackdrop,
        disableBackdropClick,
        disableEscapeKeyDown
    } = useMemo(() => {
        if (drawer) {
            const { microFront: drawerMicroFront, paper, transitionDuration, ...other } = drawer;
            const paperProps =  {
                ...paper,
                style: {
                    width: DEFAULT_DRAWER_WIDTH,
                    ...paper?.style,
                }
            };
            const transition = typeof transitionDuration === 'number' ? transitionDuration : {
                enter: DEFAULT_DRAWER_TRANSITION_DURATION,
                exit: DEFAULT_DRAWER_TRANSITION_DURATION,
            };
            
            return { drawerMicroFront, paper: paperProps, transitionDuration: transition, ...other };
        }
        
        return {};
    }, [drawer]);

    const iFrame = url ? (
        <>
            <iframe
                title="micro-front"
                ref={iframe}
                src={url}
                allow="clipboard-read; clipboard-write; fullscreen; camera; microphone; speaker; display-capture; autoplay"
                className={cx(
                    flexBox.w100,
                    flexBox.h100,
                    flexBox.borderNone,
                    flexBox.borderBox,
                    flexBox.scrollbarNone,
                    className,
                    !ready && flexBox.displayNone,
                )}
                style={resizable ? { width: size.width, height: size.height, ...style } : style}
            />
            {ready || skipLoader ? null : (
                <div
                    className={cx(
                        flexBox.flex,
                        flexBox.w100,
                        flexBox.h100,
                        flexBox.borderBox,
                        flexBox.alignItemsCenter,
                        flexBox.justifyContentCenter,
                        flexBox.minVW25,
                        flexBox.minVH25,
                    )}
                >
                    <CircularProgress size={16} thickness={4}/>
                </div>
            )}
        </>
    ) : (
        <div
            className={cx(
                flexBox.flex,
                flexBox.w100,
                flexBox.h100,
                flexBox.borderBox,
                flexBox.alignItemsCenter,
                flexBox.justifyContentCenter,
                className,
            )}
        >
            <Alert severity="error">
                {slug
                    ? `Could not found URL for micro-front ${slug}`
                    : 'At least one of micro-front properties slug or URL is required, in this case both are missing.'}
            </Alert>
        </div>
    );

    return (
        <>
            {iFrame}
            {askForConfirmation ? (
                <YesNoDialog
                    openDialog={true}
                    title={askForConfirmation.title}
                    messageToConfirm={askForConfirmation.message}
                    delayToEnable={askForConfirmation.delay}
                    yesButtonText={askForConfirmation.confirmationLabel}
                    noButtonText={askForConfirmation.cancelLabel}
                    yesButtonCallback={() => handleConfirmation(askForConfirmation.correlationId, true)}
                    noButtonCallback={() => handleConfirmation(askForConfirmation.correlationId, false)}
                />
            ) : null}
            {showConfirmation ? (
                <ConfirmationOptionsDialog
                    title={showConfirmation.title}
                    message={showConfirmation.message}
                    options={showConfirmation.options}
                    onOptionConfirmation={(option) => handleConfirmationOption(showConfirmation.correlationId, option)}
                />
            ) : null}
            {Action ? <Action onComplete={completeAction} payload={action.payload} /> : null}
            <Drawer
                key="right-drawer"
                open={!!drawerMicroFront && !closingDrawer}
                anchor="right"
                onClose={(e, reason) => {
                    if ((reason === 'backdropClick' && disableBackdropClick) || (reason === 'escapeKeyDown' && disableEscapeKeyDown)) {
                        e.stopPropagation();
                        e.preventDefault();
                    } else {
                        closeDrawer(null);
                    }
                }}
                transitionDuration={transitionDuration}
                elevation={elevation}
                hideBackdrop={hideBackdrop}
                PaperProps={paper}
                SlideProps={slide}
            >
                { drawerMicroFront ? <MicroFront {...drawerMicroFront}/> : null }
            </Drawer>
        </>
    );
};

export default MicroFront;
